import { useMemo, useState } from 'react';
import { Table, Button, Pagination, type TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type ArticleRow } from '../api/client';
import PageHeader from '../components/PageHeader';
import FilterBar from './article-list/FilterBar';
import { buildColumns, type ArticleSort } from './article-list/columns';
import ArticleCardList from './article-list/ArticleCardList';
import { useIsMobile } from '../hooks/useIsMobile';

interface Filters {
  title: string;
  status?: string;
  categoryId?: string;
  authorId?: string;
  tagId?: string;
  dateFrom?: number;
  dateTo?: number;
  page: number;
  pageSize: number;
  sort: ArticleSort;
}

function buildParams(f: Filters): Record<string, string> {
  const params: Record<string, string> = { page: String(f.page), pageSize: String(f.pageSize) };
  if (f.title) params.title = f.title;
  if (f.status) params.status = f.status;
  if (f.categoryId) params.categoryId = f.categoryId;
  if (f.authorId) params.authorId = f.authorId;
  if (f.tagId) params.tagId = f.tagId;
  if (f.dateFrom) params.dateFrom = String(f.dateFrom);
  if (f.dateTo) params.dateTo = String(f.dateTo);
  params.sortBy = f.sort.field;
  params.sortOrder = f.sort.order;
  return params;
}

export default function ArticleListPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<string>();
  const [categoryId, setCategoryId] = useState<string>();
  const [authorId, setAuthorId] = useState<string>();
  const [tagId, setTagId] = useState<string>();
  const [dateFrom, setDateFrom] = useState<number>();
  const [dateTo, setDateTo] = useState<number>();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<ArticleSort>({ field: 'updatedAt', order: 'desc' });
  const pageSize = 20;

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  const create = useMutation({
    // 標題留空（只顯示提示字）：預填「未命名草稿」會被編輯直接接著打，變成「未命名草稿＋標題」上線
    mutationFn: () => api.createArticle(''),
    onSuccess: ({ id }) => navigate(`/articles/${id}/edit`),
  });

  const params = buildParams({ title, status, categoryId, authorId, tagId, dateFrom, dateTo, page, pageSize, sort });
  const { data, isLoading } = useQuery({
    queryKey: ['articles', params],
    queryFn: () => api.listArticles(params),
  });

  const reset = () => setPage(1);
  const edit = (id: string) => navigate(`/articles/${id}/edit`);
  const columns = useMemo(() => buildColumns((id) => navigate(`/articles/${id}/edit`), sort), [navigate, sort]);
  // 點欄位標題排序；取消排序（order 為空）時回到預設「更新時間新→舊」
  const onTableChange: TableProps<ArticleRow>['onChange'] = (_p, _f, sorter, { action }) => {
    if (action !== 'sort' || Array.isArray(sorter)) return;
    const field = sorter.columnKey as ArticleSort['field'] | undefined;
    setSort(field && sorter.order ? { field, order: sorter.order === 'ascend' ? 'asc' : 'desc' } : { field: 'updatedAt', order: 'desc' });
    setPage(1);
  };
  // 捲動容器是 App 的 Content（#app-content），window.scrollTo 對它無效
  const goPage = (p: number) => {
    setPage(p);
    document.getElementById('app-content')?.scrollTo({ top: 0 });
  };
  const total = data?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="文章"
        subtitle={isLoading ? '載入中…' : `共 ${total} 篇文章`}
        extra={
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            新增文章
          </Button>
        }
      />

      <FilterBar
        categories={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        authors={(users.data ?? []).map((u) => ({ value: u.id, label: u.displayName }))}
        onTitle={(v) => { setTitle(v); reset(); }}
        onCategory={(v) => { setCategoryId(v); reset(); }}
        onAuthor={(v) => { setAuthorId(v); reset(); }}
        onTag={(v) => { setTagId(v); reset(); }}
        onStatus={(v) => { setStatus(v); reset(); }}
        onDateRange={(f, t) => { setDateFrom(f); setDateTo(t); reset(); }}
      />

      {isMobile ? (
        <>
          <ArticleCardList items={data?.items ?? []} loading={isLoading} onEdit={edit} />
          {total > pageSize && (
            <div className="mt-4 flex justify-center">
              <Pagination simple size="small" current={page} pageSize={pageSize} total={total} onChange={goPage} />
            </div>
          )}
        </>
      ) : (
        <div className="overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-sm">
          <Table<ArticleRow>
            rowKey="id"
            size="middle"
            loading={isLoading}
            columns={columns}
            onChange={onTableChange}
            dataSource={data?.items ?? []}
            rowClassName="group/row"
            styles={{ header: { cell: { fontWeight: 600 } } }}
            // 視窗太窄（平板直向）時讓表格橫向捲動，不要把欄位擠爛；寬螢幕仍撐滿 100%
            scroll={{ x: 1000 }}
            pagination={{
              current: page,
              pageSize,
              total,
              onChange: setPage,
              showTotal: (t) => `共 ${t} 篇`,
              className: 'px-4',
            }}
          />
        </div>
      )}
    </div>
  );
}
